/* dashboard/event_detection.js — vidi event detection + drip pipeline.
 *
 * Extracted from static/js/live.js on 2026-05-31 (Phase L4 of the
 * live.js refactor, see memory [[user-dashboards-architecture]]).
 *
 * Unlike LSGraphs / LSKpiMlb (pure reducers), the event-detection
 * methods are heavily stateful — they read this.prevStats, push to
 * this.feed, mutate this._vidiQueue, set this._lastVidiBatchAt, fire
 * personal-moment animations, etc. So this module is NOT
 * a pure-function library; it's a collection of methods designed to be
 * called with `this` bound to the dashboard controller. The
 * delegations in live.js look like:
 *
 *   detectEvents(...args) { return LSEventDetection.detectEvents.call(this, ...args); }
 *
 * Same pattern as the Alpine composition rule documented in
 * [[alpine-spread-kills-getters]]: methods need their original `this`
 * to maintain Alpine reactivity for writes; .call() preserves that
 * binding without copying state.
 *
 * Public API (all called with controller as `this`):
 *   detectEvents(newPlayers, localTime, myIds)
 *     → dispatches to window.SPORT_REGISTRY[sport].detectEvents
 *
 *   processMatchEvents(events, localTime, myIds)
 *     → soccer match events (Goals, Cards, VAR, Subs) from api-football
 *
 *   purgeLegacyVidiCache()
 *     → one-time localStorage cleanup; vidi feed is session-only now
 *
 *   pushVidiBatch(batch)
 *     → enrich (team_logo) + sort by gameTime + queue + kick the drip
 *
 *   vidiDrip()
 *     → drain one event from _vidiQueue, push to this.feed, schedule next
 *
 *   detectCourtEvent(p, prev, localTime, batch)
 *     → on/off court (or NHL on/off ice) transitions
 * *
 *   detectPersonalMoments(toDeltas, myPlayers)
 *     → personal-narrative animations (big swing / leader change /
 *       lineup ahead / slate over)
 *
 *   detectGameEvents(newGames, localTime)
 *     → match/inning/period transitions across games on the slate
 *
 * Controller state these methods read/write (non-exhaustive):
 *   reads:  this.selectedSlate, this.allGames, this.allPlayers,
 *           this.players, this.lineupRows, this.prevStats/prevAllStats,
 *           this.prevGames, this.vidiScope, this.scoringRules*,
 *           this._vidiRulesBySport, this.sportMeta, this._teamLogos,
 *           this._seenMatchEventIds, this.slateOver,
 *           this._moment*, this.vidiPref(), this._gameTime(),
 *           this._staggerGameTime(), this.effStatus()
 *   writes: this.feed, this._vidiQueue, this._vidiDripTimer,
 *           this.newestVidiIds, this._lastVidiBatchAt, this.slateOver,
 *           this._momentPrevBestLineupId, this._momentLineupAhead,
 *           this._momentSlateOverFired, this._seenMatchEventIds
 *   calls:  this._eventAllowsMedia(), this._playSoundForEvent(),
 *           this._pushVidiBatch(),
 *           this._vidiDrip()
 */

window.LSEventDetection = {

  // ── Detector dispatch ────────────────────────────────────────────────
  // Stat-delta event detection. Each sport's logic lives in its own
  // SPORT_REGISTRY entry (static/js/sports/<sport>.js). NBA's detector
  // serves as the fallback for any sport without a registered detectEvents
  // (e.g. an experimental basketball-shape detector — in practice they
  // all hit the engine path since they have DB rules).
  detectEvents(newPlayers, localTime, myIds) {
    const sport = (this.selectedSlate?.sport_id || 'nba').toLowerCase();
    const fn    = window.SPORT_REGISTRY[sport]?.detectEvents
               || window.SPORT_REGISTRY.nba.detectEvents;
    return fn.call(this, newPlayers, localTime, myIds);
  },

  // Server-scored scorer events (2026-07-05). The engine + scoring moved
  // server-side (core/vidi_engine.py, derived in the poller, scored per-platform
  // in core/slate_live.py), so the browser no longer computes points — it just
  // adapts data.scorer_events to the feed shape and drips them through the same
  // path detectEvents used. The server resends the recent buffer each tick, so
  // we skip ids already in the feed. Scope ('mine' vs 'all') + drip/animation
  // stay client-side. `evtPlayers` is the scoped player list (for the running
  // total + a freshness read).
  /* How many events the feed keeps.
   *
   * Was 20. Moments (`achievement` / `big_play`) are filtered out of this feed,
   * and they are rare - so on arrival the newest 20 events usually contain none
   * and BIG MOMENTS sat empty while the server had just sent the whole game.
   *
   * The WIRE does not show more because of this: it renders
   * `slice(0, capacity())`, fill-to-height. The depth is for what gets FILTERED
   * out of the feed, not what gets drawn from it.
   */
  VIDI_FEED_CAP: 150,

  /* A batch bigger than this is history, not live action, so it is committed in
   * one go instead of dripped. One poll cycle across a full slate produces a
   * handful of events; dozens at once means a backlog - a first push that
   * arrived empty, an SSE reconnect, or a tab that was in the background.
   * Dripping those replays the night one animation and one sound at a time.
   */
  VIDI_BURST: 10,

  ingestScorerEvents(scorerEvents, localTime, myIds, evtPlayers, seedMode) {
    if (!Array.isArray(scorerEvents) || !scorerEvents.length) return;
    // Persistent seen-set: the server resends the whole buffer each tick and
    // this.feed is capped at 20, so deduping against the feed alone would let
    // events that aged out re-drip forever. Slate-scoped (reset in selectSlate),
    // trimmed to keep it bounded on long games.
    if (!this._scorerSeenIds) this._scorerSeenIds = new Set();
    // Bound it on long games — once large, reseed from the current buffer (older
    // ids are already dripped and capped out of the feed).
    if (this._scorerSeenIds.size > 4000) {
      this._scorerSeenIds = new Set(scorerEvents.map(e => e && e.id).filter(Boolean));
    }
    const seen = this._scorerSeenIds;
    const totalByPid = {};
    for (const p of (evtPlayers || [])) totalByPid[p.player_id] = p.live_fpts || 0;
    const gameTime = (gid) => {
      const g  = (this.allGames || []).find(x => x.game_id === gid);
      const ls = g && g.live_state;
      if (!ls || !ls.inning) return '';
      return (ls.is_top === false ? 'B' : 'T') + ls.inning;
    };
    const scopedAll = this.vidiScope === 'all';
    const batch = [];
    for (const ev of scorerEvents) {
      if (!ev || !ev.id || seen.has(ev.id)) continue;
      // Score-change bands are GAME-level, not tied to a rostered player — never
      // drop them on the 'mine' scope filter (like match-event dividers).
      const isGameEvt = ev.event_type === 'score';
      const inLineup = myIds ? myIds.has(ev.player_id) : true;
      if (!scopedAll && !inLineup && !isGameEvt) continue;   // 'mine' → my players only
      seen.add(ev.id);
      batch.push({
        id:         ev.id,
        _pid:       String(ev.player_id || ''),
        localTime,
        // Inning/quarter badge: write-time game_state stamp (pbp "T8"/"B8"),
        // then the event's own gameTime (score bands carry "Q2 8:48"), then live.
        gameTime:   (ev.game_state && ev.game_state.inn) || ev.gameTime || gameTime(ev.game_id),
        name:       ev.name,
        team:       ev.team,
        // The id, not just the label - anything matching this event to its
        // game must join on it (feedback_no_abbrev_matching_dashboard).
        team_id:    ev.team_id,
        // Score-change band payload (event_type 'score') — passed through so the
        // renderer can draw the mini-scoreboard. Undefined on normal rows.
        headline:   ev.headline,
        points:     ev.points,
        score_side: ev.score_side,
        away_abbr:  ev.away_abbr,  home_abbr:  ev.home_abbr,
        away_name:  ev.away_name,  home_name:  ev.home_name,
        away_logo:  ev.away_logo,  home_logo:  ev.home_logo,
        away_score: ev.away_score, home_score: ev.home_score,
        // Keep both: `label` is the terse play type ("Single"), `desc` is the
        // borrowed pbp narrative (falls back to the label when uncorrelated).
        // The 2-line wire card shows both.
        label:      ev.label,
        desc:       ev.desc || ev.label,
        vs:         ev.vs,           // counterpart batter/pitcher for the row
        salience:   ev.salience,
        fpts:       ev.fpts,
        // Player running total AS OF this play. The server derives it by
        // walking the event window back from the player's current score
        // (stamp_running_totals in core/slate_live.py); take that when it is
        // there. `totalByPid` is the player's total RIGHT NOW - the same
        // number on every one of his events, so two plays in a row read
        // "+0.60 18.70 / +1.00 18.70" and the column says nothing. It stays
        // only as the fallback for a snapshot written before the server
        // started stamping, where a stale total still beats a blank one.
        // Null when the player isn't in the scoped pool (unknown) so the card
        // can distinguish that from a real 0/negative.
        total:      ev.total != null ? ev.total
                      : (Object.prototype.hasOwnProperty.call(totalByPid, ev.player_id)
                           ? totalByPid[ev.player_id] : null),
        in_lineup:  inLineup,
        event_type: ev.event_type,
        sound_key:  ev.sound,
        rule_id:    ev.rule_id,
        game_id:    ev.game_id,
        // Dedup key against the pbp narrative for the same at-bat.
        game_pk:    ev.game_pk,
        at_bat_index: ev.at_bat_index,
        game_state: ev.game_state,   // {a,as,h,hs,inn} — Moments-lane context
      });
    }
    // First apply for this slate: the whole buffer is historical, so commit the
    // newest slice STATICALLY (one assignment, no drip → no per-event sound or
    // moment popup) instead of replaying the night. All batch ids are already in
    // `seen`, so nothing re-drips next tick. Live events after open drip normally.
    // LSEventDetection.*, not this.* — see the module header. Neither
    // _seedVidiFeed nor _vidiGtRank is delegated by live.js, so off `this`
    // (the controller) they are undefined.
    if (seedMode) { LSEventDetection._seedVidiFeed.call(this, batch); return; }
    // A backlog, not live action — commit it the same way the seed does rather
    // than dripping it. See VIDI_BURST.
    if (batch.length > LSEventDetection.VIDI_BURST) {
      LSEventDetection._seedVidiFeed.call(this, batch, LSEventDetection.VIDI_FEED_CAP, true);
      return;
    }
    if (batch.length) this._pushVidiBatch(batch);
  },

  // Static feed seed for the first apply — newest N events, newest-first, no
  // animation/sound. See ingestScorerEvents + _vidiFeedRender's instant paint.
  _seedVidiFeed(batch, N, merge) {
    if (!batch || !batch.length) return;
    if (N == null) N = LSEventDetection.VIDI_FEED_CAP;
    const logos = this._teamLogos;
    for (const ev of batch) {
      const T = (ev.team || '').toUpperCase();
      if (T && !ev.team_logo) ev.team_logo = logos[T] || '';
    }
    // Chronological ascending, then take the newest N and reverse to newest-first.
    // _vidiOrder, not _vidiGtRank alone: a cold load seeds the whole backlog in
    // one go, and the score/play tie-break has to hold there too or the feed a
    // mid-slate arrival READS has points cards above their own score changes -
    // which is the only Wire most visitors ever see.
    // `merge` is the mid-stream burst: the feed already holds events this batch
    // does not, so replacing it would drop them. The cold-open seed has nothing
    // to keep and replaces outright.
    const base = merge ? batch.concat(this.feed.slice().reverse()) : batch;
    const seen = new Set();
    const uniq = base.filter(e => e && e.id && !seen.has(e.id) && seen.add(e.id));
    const sorted = uniq.sort(LSEventDetection._vidiOrder);
    this.feed = sorted.slice(-N).reverse();
    this.newestVidiIds = {};   // nothing "new" to flash on a static seed
  },

  // Rank an event's gameTime string for chronological ordering (earlier = lower).
  // Shared by the drip funnel and the static seed.
  _vidiGtRank(gt) {
    const m = (gt || '').match(/^[A-Z]+(\d+)\s+(\d{1,2}):(\d{2})$/);
    if (!m) return Number.MAX_SAFE_INTEGER;
    const period = parseInt(m[1], 10);
    const mins   = parseInt(m[2], 10);
    const secs   = parseInt(m[3], 10);
    return period * 100000 - (mins * 60 + secs);
  },

  processMatchEvents(events, localTime, myIds) {
    if (!events || !events.length) return;
    if (!this.vidiPref('soccer', 'pitch_events', true)) return;
    const batch = [];
    const _SOUND = {
      "Goal":                 "soccer_goal",
      "Penalty":              "soccer_penalty",
      "Own Goal":             "soccer_owngoal",
      "Missed Penalty":       "soccer_missed_pen",
      "Yellow Card":          "soccer_card",
      "Red Card":             "soccer_card",
      "Second Yellow":        "soccer_card",
      "VAR Goal Cancelled":   "soccer_var",
      "VAR Penalty Confirmed":"soccer_var",
      "Substitution":         "default",
    };
    // Map AF event type → scoring rule stat key. Keys must match scoring_rules.stat
    // (yellow/red, NOT yellow_card/red_card — those don't exist in the DB).
    // Penalty = scored goal (+10). Second Yellow = effectively a red.
    const _EV_STAT = {
      'Goal':          'goal',
      'Penalty':       'goal',
      'Assist':        'assist',
      'Yellow Card':   'yellow',
      'Red Card':      'red',
      'Second Yellow': 'red',
    };
    // Events that have no fpts effect on the named player — show on feed
    // but suppress the fpts column (avoids confusing "+0.00" rendering).
    const _NO_FPTS_EVENTS = new Set([
      'Substitution',
      'VAR Goal Cancelled',
      'VAR Penalty Confirmed',
      'Missed Penalty',
      'Own Goal',
    ]);
    const _posGroup = pos => ({GK:'gk',G:'gk',DEF:'def',D:'def',MID:'mid',M:'mid',FWD:'fwd',F:'fwd',ATT:'fwd'}[(pos||'').toUpperCase()] || '');
    const _evFpts = (evType, pos) => {
      const stat = _EV_STAT[evType];
      if (!stat) return 0;
      const byPos = this.scoringRulesByPos || {};
      const posGroups = Object.fromEntries(Object.entries(byPos).filter(([k]) => !k.startsWith('_')));
      const pg = _posGroup(pos);
      const rules = (pg && posGroups[pg]) ? posGroups[pg]
                  : Object.keys(posGroups).length ? Object.assign({}, ...Object.values(posGroups)) : this.scoringRules;
      return rules[stat] || 0;
    };
    // Match player by LiveSlate player_id (UUID) first, then name as fallback for unmapped players
    const _findPlayer = (playerId, name, pool) => {
      if (playerId) {
        const sid = String(playerId);
        const byId = pool.find(p => p.player_id && String(p.player_id) === sid);
        if (byId) return byId;
      }
      if (!name) return null;
      const norm = name.toLowerCase().replace(/[^a-z0-9]/g, '');
      return pool.find(p => p.name && p.name.toLowerCase().replace(/[^a-z0-9]/g, '').includes(norm.slice(0, 6))) || null;
    };
    // Search the full slate pool first, fall back to lineup players. (Field is
    // allSlatePlayers — `this.allPlayers` was a typo that left non-lineup
    // players' live events unresolved: 0 fpts, no scoring context.)
    const _allPool = [...(this.allSlatePlayers || []), ...(this.players || [])];

    for (const ev of events) {
      if (this._seenMatchEventIds.has(ev.id)) continue;
      this._seenMatchEventIds.add(ev.id);

      // Goal Pending — api-football announces a goal before the scorer is
      // confirmed (Sky Sports vibe). Render with friendly text + suppress
      // fpts display by setting event_type. When the scorer resolves on a
      // later poll, a normal Goal event fires fresh (different dedup key).
      if (ev.type === 'Goal Pending') {
        batch.push({
          id:        `af-${ev.id}`,
          localTime,
          gameTime:  ev.time_str || '',
          name:      `GOAL! ${ev.team_name || ''}`,
          team:      ev.team_name || '',
          desc:      'awaiting scorer…',
          fpts:      0,
          total:     0,
          in_lineup: false,
          event_type: 'pending',
          sound_key:  _SOUND['Goal'] || 'default',
        });
        continue;
      }

      const player   = _findPlayer(ev.player_id, ev.player_name, _allPool);
      const inLineup = (this.players || []).some(p => p === player);
      const fpts     = _evFpts(ev.type, player ? player.pos : '');
      const total    = player ? (player.live_fpts || 0) : 0;

      let desc = ev.type;
      if (ev.type === 'Substitution' && ev.assist_name) desc = `Sub off → ${ev.assist_name}`;

      // Suppress fpts column for events that don't affect the named player's
      // score (subs, VAR confirmations, etc.) — "+0.00" looked buggy.
      const eventType = _NO_FPTS_EVENTS.has(ev.type) ? 'info' : null;

      batch.push({
        id:        `af-${ev.id}`,
        localTime,
        gameTime:  ev.time_str || '',
        name:      ev.player_name || 'Unknown',
        team:      ev.team_name || '',
        desc,
        fpts,
        total,
        in_lineup: inLineup,
        event_type: eventType,
        sound_key:  _SOUND[ev.type] || 'default',
      });

      if (ev.type === 'Goal' && ev.assist_name && ev.assist_name.toLowerCase() !== 'unknown') {
        const aplayer   = _findPlayer(ev.assist_player_id, ev.assist_name, _allPool);
        const aInLineup = (this.players || []).some(p => p === aplayer);
        batch.push({
          id:        `af-${ev.id}-assist`,
          localTime,
          gameTime:  ev.time_str || '',
          name:      ev.assist_name,
          team:      ev.team_name || '',
          desc:      'Assist',
          fpts:      _evFpts('Assist', aplayer ? aplayer.pos : ''),
          total:     aplayer ? (aplayer.live_fpts || 0) : 0,
          in_lineup: aInLineup,
          event_type: null,
          sound_key:  'default',
        });
      }
    }
    if (batch.length) this._pushVidiBatch(batch);
  },

  // The vidi feed used to persist to localStorage and restore on every
  // page load / slate switch. Removed: it confused users — already-seen
  // events with running totals (e.g. "Game Lost (Total 5)") would
  // reappear on reload looking like 5 fresh events. The feed is now
  // session-only, starting empty when the dashboard loads.
  purgeLegacyVidiCache() {
    try {
      const stale = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        // ls_vidi_density_phone is the phone Wire's Mine/All choice, not a
        // cached feed: purging it reset the phone to Mine on every load
        if (k && k.startsWith('ls_vidi_') && k !== 'ls_vidi_density_phone') stale.push(k);
      }
      for (const k of stale) localStorage.removeItem(k);
    } catch (_) {}
  },

  pushVidiBatch(batch) {
    if (!batch.length) return;
    // Enrich every event with team_logo right at the funnel point so the
    // template doesn't have to do reactive lookups via _teamLogos. Resolves
    // via allSlatePlayers first (server-enriched), then games (always have
    // home_logo / away_logo populated).
    const logos = this._teamLogos;
    for (const ev of batch) {
      const T = (ev.team || '').toUpperCase();
      if (T && !ev.team_logo) ev.team_logo = logos[T] || '';
    }
    // Sort by game time (chronological) so events drip out in the order they
    // actually happened. _vidiGtRank: earlier = lower period and/or more clock
    // remaining. Stable sort keeps same-gameTime events in source order.
    // Array.prototype.sort never calls the comparator for 0 or 1 elements,
    // which is why this survived: live play usually delivers ONE event per
    // tick. It threw only on multi-event batches — i.e. a cold load of a
    // finished slate, where the whole night arrives at once.
    // THE SCORE CHANGE LANDS BEFORE THE PLAY THAT CAUSED IT. Judge hits a home
    // run and two things happen at the same game time: NYY's score goes up by
    // one, and Judge is +10. They are different kinds of event - a score update
    // is not an FPts event and is not trying to be - but they go hand in hand,
    // and the score is the thing that happened first in the world (Xav,
    // 2026-09-19). Same game time means the comparator never separated them, so
    // the order was whatever the server sent. Tie-break on it.
    batch.sort(LSEventDetection._vidiOrder);
    // CATCH-UP IS SILENT. Events arriving in the frame held while the phone
    // was locked, or in the first push after a reconnect, already happened
    // while nobody was looking: they belong in the feed, not in your ear.
    // Waking the phone played one sound per missed event (run 8, LIVE-1).
    // The mark travels WITH the event - the sound fires at drip time, long
    // after the flag that caused it has been cleared.
    if (this._catchUpApply) for (const ev of batch) ev.catch_up = true;
    this._vidiQueue.push(...batch);
    // Sort the QUEUE, not just this batch. Nothing in it has been shown yet, so
    // reordering costs nothing - and a score change and the play that caused it
    // do not always arrive in the same push, which is the whole reason sorting
    // the batch alone left points cards overtaking their own score change.
    this._vidiQueue.sort(LSEventDetection._vidiOrder);
    this._lastVidiBatchAt = Date.now();
    if (!this._vidiDripTimer && !this._vidiDripPaused) this._vidiDrip();
  },

  /** Feed order: by game time, and within one game time the SCORE CHANGE
   *  first. Judge hits a home run and two things happen at the same moment -
   *  NYY's score goes up by one, and Judge is +10. They are different kinds of
   *  event and both belong, but the score is what happened first in the world
   *  (Xav, 2026-09-19). Same game time meant the comparator never separated
   *  them, so the order was whatever the server sent. */
  _vidiOrder(a, b) {
    const d = LSEventDetection._vidiGtRank(a.gameTime) - LSEventDetection._vidiGtRank(b.gameTime);
    if (d) return d;
    // ONLY a genuine pair: same game, same game time. The tie-break cannot be
    // applied to every tie, because _vidiGtRank only understands a clock format
    // ("Q1 5:09") - baseball's "T1"/"B2" matches nothing, so EVERY MLB event
    // ranks MAX_SAFE_INTEGER and the whole feed is one tie held in source order.
    // Ordering all of those score-first marched every score card to the top of
    // the Wire, ahead of plays from earlier innings. Returning 0 keeps source
    // order, which is what the stable sort has always relied on here.
    if ((a.gameTime || '') !== (b.gameTime || '')) return 0;
    if (String(a.game_id || '') !== String(b.game_id || '')) return 0;
    return (a.event_type === 'score' ? 0 : 1) - (b.event_type === 'score' ? 0 : 1);
  },

  vidiDrip() {
    this._vidiDripTimer = null;
    // While the active replay is paused, freeze the drip — the queue is
    // preserved and resumes draining when the user hits Resume.
    if (this._vidiDripPaused) return;
    if (!this._vidiQueue.length) return;
    const ev = this._vidiQueue.shift();
    const existingIdx = this.feed.findIndex(e => e.id === ev.id);
    if (existingIdx === -1) {
      // A genuinely-new event is dripping — from here on lane paints animate
      // (the historical seed rendered instant while this was false).
      this._vidiHadLiveEvent = true;
      this.feed = [ev, ...this.feed].slice(0, LSEventDetection.VIDI_FEED_CAP);
      this.newestVidiIds = { [ev.id]: true };
      // A big play by one of MY players rings (sounds stay gated to my
      // players to avoid sonic clutter on busy slates). The in-card event
      // animation was retired 2026-10-11: it could only show on a slate of
      // four games or fewer, in Cards view, on a desktop - the Wire has
      // every play, and the personal moments below carry the big ones.
      // Hockey: every goal on the scoreboard sounds the goal horn (Xav
      // 2026-10-16, "C" from the goal horn lab) - the score band is the goal,
      // whoever scored it. The scorer's own big_play row then stays quiet,
      // so one goal is one horn, not a horn and a tick.
      const _sport = String((this.selectedSlate && this.selectedSlate.sport_id) || '').toLowerCase();
      if (_sport === 'nhl' && ev.event_type === 'score') {
        if (!ev.catch_up) this._playSoundForEvent('nhl_goal_horn');
      } else if (_sport === 'nhl' && ev.rule_id === 'goal') {
        // the horn already went with the score band
      } else if (!ev.catch_up && ev.in_lineup && this._eventAllowsMedia(ev)) {
        // Pass both keys (legacy sound_key 'nba_2pt', engine rule_id '2pt_made').
        this._playSoundForEvent(ev.sound_key || 'default', ev.rule_id);
      }
    } else if (ev.fpts !== 0 && this.feed[existingIdx].fpts === 0) {
      // Existing entry had no points (rare race) — update with correct value, no sound replay
      this.feed = this.feed.map((e, i) => i === existingIdx ? ev : e);
    }
    if (this._vidiQueue.length) {
      // Adaptive spacing: drain this batch quickly (a live feed shouldn't hold a
      // home run back ~20s) while still spacing the sound + moment popups so they
      // don't all fire in one frame. Floor keeps them distinct on a spike.
      const TARGET_DRAIN_MS = 6000;  // drain a batch in ~6s, then idle to next poll
      const MIN_DRIP_MS     = 500;
      const elapsed   = Date.now() - this._lastVidiBatchAt;
      const remaining = Math.max(0, TARGET_DRAIN_MS - elapsed);
      const spacing   = Math.max(MIN_DRIP_MS, remaining / this._vidiQueue.length);
      this._vidiDripTimer = setTimeout(() => this._vidiDrip(), spacing);
    }
  },

  detectCourtEvent(p, prev, localTime, batch) {
    const sport = (this.selectedSlate?.sport_id || '').toLowerCase();
    // NHL uses 'onice_events'; everyone else uses 'court_events'.
    const leaf = sport === 'nhl' ? 'onice_events' : 'court_events';
    if (!this.vidiPref(sport, leaf, true)) return;
    if (prev.oncourt === undefined || prev.oncourt === p.oncourt) return;
    batch.push({
      id: `court-${p.player_id}-${Date.now()}`,
      localTime, gameTime: this._gameTime(p),
      name: p.name, team: p.team, team_logo: p.team_logo,
      desc: p.oncourt ? this.sportMeta.on : this.sportMeta.off,
      fpts: 0, total: p.live_fpts,
      in_lineup: true,
      event_type: p.oncourt ? "oncourt" : "offcourt",
    });
  },


  // ── Personal moments ──────────────────────────────────────────────────
  // Fires the existing headline-engine animation system for moments
  // derived from live SSE state (not from platform-pushed events). Works
  // for any user on any platform — no FT-extension / DK-connector needed.
  // Every detector is EDGE-triggered: each fires once at the transition,
  // not every tick the state persists.
  detectPersonalMoments(toDeltas, myPlayers) {
    const eng = window.VidiHeadlineEngine;
    if (!eng || !eng.render) return;

    // THIS LAYER HAS NEVER RENDERED ANYTHING. The engine requires a target -
    // fullscreen was retired, and it refuses to fall back to <body> - and this
    // call passed none, so every personal moment returned false: 4 calls, 0
    // overlays, measured on a live board (audit LIVE-11, 2026-09-16).
    // The phone's target is the hero, which is where your own number lives,
    // in the layout that keeps that number in view; the desktop's is the
    // scorecard if one is mounted.
    //
    // Found at the moment it fires, and only one you can see: not a hidden
    // hero (another tab), not one scrolled away under the context bar or off
    // the bottom. A moment that plays out of view is over before you're back
    // (run 8, LIVE-12) - the Wire still has the play. Resolving it here rather
    // than up front also keeps the state below moving while you're elsewhere,
    // so nothing fires late on your return.
    const momentTarget = () => {
      if (this.shellMode) {
        const hero = Array.from(document.querySelectorAll('.msh-hero')).find(h => h.getClientRects().length);
        if (!hero) return null;
        const r = hero.getBoundingClientRect();
        const bar = document.querySelector('.msh-ctx');
        const top = bar ? bar.getBoundingClientRect().bottom : 0;
        const mid = (r.top + r.bottom) / 2;
        if (mid <= top || mid >= window.innerHeight) return null;
        return { target: hero, layout: 'hero' };
      }
      // Broadcast: the Director owns the big moments, and "the first
      // .ls-widget" there is whichever panel the tab happens to start with -
      // BIG SWING blurred Paths to the Top, Your Players and the Wire in turn
      // (audit run 1, LIVE-3; Xav P5).
      if (this.activeSkin === 'broadcast') return null;
      const card = document.querySelector('[data-widget-id="scorecard"]') ||
                   document.querySelector('.ls-widget');
      return card ? { target: card } : null;
    };

    // ...and it would have MISFIRED the moment it worked. On a cold load the
    // previous-points map is empty, so the whole night reads as one delta:
    // BIG SWING fired for three players within 3ms of the page opening. The
    // first apply seeds the baseline and says nothing.
    //
    // SEED EVERYTHING, NOT JUST THE POINTS. Skipping the first apply was half
    // of it: the edge-triggered maps below were still empty, so on the SECOND
    // update every lineup already ahead of pace "crossed" at once - "AHEAD OF
    // PACE" 0.4s after arrival, twice (run 8, LIVE-11 / DFS-15). The first
    // pass now records the state it found, and fires nothing.
    //
    // The card's own progress gate (pace_frac ≥ 0.25, the same floor players
    // get) applies here too: the overlay fired at pace_frac 0.026 while the
    // card it sat above said nothing.
    const aheadOf = (lu) => {
      const paced = lu.paced_proj || 0, live = lu.live_total || 0;
      return paced > 0 && (lu.pace_frac || 0) >= 0.25 && live >= 20 && live >= paced * 1.15;
    };
    const allFinishedNow = () => {
      const gs = this.allGames || [];
      return gs.length > 0 && gs.every(g => this.effStatus(g) >= 3);
    };
    if (!this._pmSeeded) {
      this._pmSeeded = true;
      // Nothing for 10s after a (re)seed: a cold load, a slate switch, a demo
      // seek or restart. The first screen is for reading the board.
      this._pmQuietUntil = Date.now() + 10000;
      this._momentLineupAhead = {};
      for (const lu of (this.lineupRows || [])) this._momentLineupAhead[lu.id] = aheadOf(lu);
      // Opening a board that has already finished is not the moment it ended.
      this._momentSlateOverFired = allFinishedNow();
      return;
    }

    const quiet = Date.now() < (this._pmQuietUntil || 0);
    const fire = (key, info) => {
      if (quiet) return;          // state below still updates, so nothing fires late
      const where = momentTarget();
      if (!where) return;
      try { eng.render(key, info || {}, where); } catch (_) { /* never let an animation crash the dashboard */ }
    };
    // The demo's lineups are a stranger's showcase, not the visitor's: a
    // lineup-level "AHEAD OF PACE · MODERN MASH" means nothing to someone who
    // has never built one, and the demo has its own end card. Big swings stay
    // - they are the feature being shown.
    const ownLineups = !this.demoShowcase;

    // 1. Big swing — any of my players ticked ≥ 8 FPts in one update.
    //    Reuses toDeltas computed above; no extra state needed because
    //    each tick gives a fresh delta map.
    for (const [pid, delta] of Object.entries(toDeltas || {})) {
      if ((delta || 0) < 8) continue;
      const p = myPlayers.find(x => String(x.player_id) === String(pid));
      if (!p) continue;
      fire('personal_big_swing', {
        player_name: p.name,
        team_name:   p.team,
        fpts:        delta,
        sport:       this.selectedSlate?.sport_id || '',
      });
    }

    // (2. "NEW LEADER" is gone. Every lineup it compared was the user's own,
    //  so it announced one of your entries passing another - which reads as a
    //  contest lead, the one thing the product must never imply. Run 8, DFS-15.)
    const newBest = [...(this.lineupRows || [])].sort(
      (a, b) => (b.live_total || 0) - (a.live_total || 0),
    )[0];

    // 3. Lineup crossed ≥ 115% of paced projection, past the progress gate.
    //    Edge-triggered via _momentLineupAhead, so it fires once per crossing
    //    and only again after the lineup has dropped back below pace. The
    //    figure is the MARGIN over pace - the thing that happened - never the
    //    lineup's total dressed up as a gain ("+33.6 FPTS").
    for (const lu of (this.lineupRows || [])) {
      const isAhead  = aheadOf(lu);
      const wasAhead = !!this._momentLineupAhead[lu.id];
      if (isAhead && !wasAhead && ownLineups) {
        fire('personal_lineup_ahead', {
          player_name: lu.lineup_name,
          fpts:        (lu.live_total || 0) - (lu.paced_proj || 0),
        });
      }
      this._momentLineupAhead[lu.id] = isAhead;
    }

    // 4. Slate over — every game finished. One-shot per slate. Uses effStatus
    // (not raw status) so a provider stuck at 2 past a game's end window
    // doesn't suppress "SLATE OVER" forever. Its figure is the best lineup's
    // TOTAL, so it carries no "+" - it is the number on the hero above it. No
    // lineup, no figure: it printed "0.00" to anyone watching without one.
    if (allFinishedNow() && !this._momentSlateOverFired) {
      this._momentSlateOverFired = true;
      if (ownLineups && newBest) {
        fire('personal_slate_over', {
          player_name: this.selectedSlate?.name || 'Slate',
          fpts:        newBest?.live_total || 0,
          fpts_total:  true,
        });
      }
    }
  },

  detectGameEvents(newGames, localTime) {
    const sport = (this.selectedSlate?.sport_id || 'nba').toLowerCase();
    const matchEventsOn = this.vidiPref(sport, 'match_events', true);
    if (!matchEventsOn) {
      // Still detect slateOver even when match events are muted.
      if (!this.slateOver && newGames.length > 0 && newGames.every(g => this.effStatus(g) === 3)) {
        this.slateOver = true;
        LSEventDetection._keepTheNight.call(this);
      }
      return;
    }
    // THE GAMES ON THE BOARD, not the league. A demo slate carries filler games
    // to look like a real evening, and their inning dividers were a third of the
    // Wire - "Inning 5" for a game with nobody's players in it, between the plays
    // a visitor came to watch (run 8, first contact #6). With no lineup at all
    // (a spectator) every game still counts: there is nothing else to narrow to.
    const mine = new Set();
    for (const lu of (this.lineupRows || [])) {
      for (const pl of (lu.players || [])) if (pl.game_id) mine.add(String(pl.game_id));
    }
    const batch = [];
    for (const g of newGames) {
      const prev = this.prevGames[g.game_id];
      if (!prev) continue;
      if (mine.size && !mine.has(String(g.game_id))) continue;
      const label      = `${g.away_team} @ ${g.home_team}`;
      const gameTime   = (g.status_text || "").replace(/^(\S+)\s+(\d):/, "$1 0$2:");
      const prevPeriod = prev.period || 0;
      const curPeriod  = g.period   || 0;
      let desc = null, et = "start";

      if (prev.status === 1 && g.status === 2) {
        desc = sport === 'mlb' ? "Game Started" : "Match Started";
      } else if (prev.status === 2 && g.status === 3) {
        desc = sport === 'mlb' ? "Game Ended" : "Match Ended"; et = "end";
      } else if (g.status === 2 && curPeriod > prevPeriod) {
        if (sport === 'mlb') {
          desc = `Inning ${curPeriod}`;
        } else {
          const stTxt   = (g.status_text  || "").toLowerCase();
          const prevTxt = (prev.status_text || "").toLowerCase();
          if (!prevTxt.includes("half") && stTxt.includes("half")) { desc = "Half Time"; et = "end"; }
          else if (curPeriod === 3) { desc = sport === 'nhl' ? "3rd Period" : "3rd Quarter"; }
          else if (curPeriod === 5) { desc = "Extra Time"; et = "ot"; }
          else if (curPeriod > 5)   { desc = `Extra Time ${curPeriod - 4}`; et = "ot"; }
        }
      }

      if (desc) {
        batch.push({ id: `game-${g.game_id}-${curPeriod}-${et}`, localTime, gameTime,
          name: label, team: "", desc, fpts: 0, total: 0, in_lineup: false, event_type: et });
      }
    }
    if (batch.length) {
      if (this._catchUpApply) for (const ev of batch) ev.catch_up = true;
      this._vidiQueue.push(...batch);
      this._lastVidiBatchAt = Date.now();
      if (!this._vidiDripTimer) this._vidiDrip();
    }
    // Detect slate over
    if (!this.slateOver) {
      const allGames = newGames.length > 0 ? newGames : Object.values(this.prevGames);
      if (allGames.length > 0 && allGames.every(g => this.effStatus(g) === 3)) {
        this.slateOver = true;
        LSEventDetection._keepTheNight.call(this);
      }
    }
  },

  /** The final whistle ends the drip, not the Wire. This used to empty the
   *  feed, so the night's commentary vanished the moment the last game ended
   *  and the Wire read "No new events yet." - on the public demo too
   *  (Broadcast audit v2 run 3, LIVE-1). What is still queued lands at once,
   *  quietly (no sound or popup for plays the drip had not reached). */
  _keepTheNight() {
    if (this._vidiDripTimer) { clearTimeout(this._vidiDripTimer); this._vidiDripTimer = null; }
    const left = (this._vidiQueue || []).splice(0);
    if (left.length) {
      const have = new Set((this.feed || []).map(e => e && e.id));
      const add = left.filter(e => e && !have.has(e.id)).reverse().map(e => Object.assign({}, e, { catch_up: true }));
      this.feed = [...add, ...(this.feed || [])].slice(0, LSEventDetection.VIDI_FEED_CAP);
    }
    this.newestVidiIds = {};
  },

};
